/**
 * Production reports API client — Sprint 0143 PREP-14.
 *
 * 8 can-stock + 14 MIS Excel exports. Backend serves these at
 * /production/reports/{can-stock|mis}/{name}.xlsx.
 *
 * Each download triggers a browser save dialog via a blob URL — we
 * never store the binary in app state.
 */
import apiClient from '@/shared/api/client'

export type ReportKind = 'can-stock' | 'mis'

export interface ReportInfo {
  name: string
  title: string
  description: string
}

export interface ReportsCatalogue {
  can_stock: ReportInfo[]
  mis: ReportInfo[]
}

export interface ReportParams {
  plant_id: string
  on_date?: string        // YYYY-MM-DD
  from_date?: string      // YYYY-MM-DD
  to_date?: string        // YYYY-MM-DD
  run_id?: string         // for manpower MIS
}

/** Pull the list of available reports (used to render the button grids). */
export async function fetchReportsCatalogue(): Promise<ReportsCatalogue> {
  const res = await apiClient.get<ReportsCatalogue>(
    '/production/reports/catalogue',
  )
  return res.data
}

/**
 * Download one report. Triggers a browser save by creating a blob URL
 * + clicking a hidden anchor. We never keep the bytes around — once
 * the click fires the URL is revoked.
 *
 * Returns the filename written (handy for toast messages).
 */
export async function downloadReport(
  kind: ReportKind,
  reportName: string,
  params: ReportParams,
): Promise<string> {
  const res = await apiClient.get<Blob>(
    `/production/reports/${kind}/${reportName}.xlsx`,
    {
      params,
      responseType: 'blob',
    },
  )

  // Derive filename — backend sets Content-Disposition but axios may
  // not surface it cleanly across browsers. Fall back to a sensible
  // computed name.
  const cd: string = res.headers['content-disposition'] || ''
  const match = cd.match(/filename="?([^";]+)"?/)
  const filename = match ? match[1] : `${kind}_${reportName}.xlsx`

  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Let the browser commit the download before we tear the URL down.
  setTimeout(() => URL.revokeObjectURL(url), 100)
  return filename
}
